import { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  BookOpen,
  Copy,
  Download,
  Eye,
  Loader2,
  Pencil,
  PlusCircle,
  RefreshCw,
  SquarePlus,
  Trash2,
  X,
} from "lucide-react";
import { useTheme } from "../../../../components/common/Theme/ThemeContext";
import { dataModelsCatalogService, type DataModelCatalogRow } from "../dataModelsCatalogService";
import { featureHelperService, type KnowledgeBaseListItem } from "../MCPResources/featureHelperService";
import {
  findCredentialsForApplication,
  resourceAccessService,
  type ApiResourceType,
  type ResourceAccessCredentialDetail,
  type ResourceAccessCredentialListItem,
  type ResourceAccessGroup,
} from "./resourceAccessService";
import { isRFC4122Guid, isResourceAccessResourceId } from "../guidUtils";

const GRADIENTS = [
  "from-violet-600 to-indigo-500",
  "from-orange-600 to-yellow-500",
  "from-teal-600 to-blue-500",
  "from-purple-600 to-pink-500",
  "from-orange-500 to-red-500",
  "from-teal-500 to-emerald-400",
  "from-zinc-600 to-zinc-400",
];

function initFromName(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

function statusLooksRevoked(status: string): boolean {
  const s = status.toLowerCase();
  return s.includes("revok") || s.includes("inactive") || s === "disabled" || s === "suspended";
}

function formatApiDate(iso: string): string {
  const t = (iso || "").trim();
  if (!t) return "—";
  const d = new Date(t);
  return Number.isNaN(d.getTime()) ? t : d.toLocaleString();
}

function credentialExportPayload(d: ResourceAccessCredentialDetail): Record<string, unknown> {
  return {
    id: d.id,
    clientId: d.clientId,
    clientSecret: d.clientSecret,
    accessToken: d.accessToken,
    status: d.status,
    isActive: d.isActive,
    expiry: d.expiry,
    expiresAt: d.expiresAt,
    rateLimitPerMinute: d.rateLimitPerMinute ?? null,
    label: d.label,
  };
}

function downloadCredentialJson(d: ResourceAccessCredentialDetail): void {
  const safeLabel = (d.label || d.id || "credential").replace(/[^a-z0-9-_]+/gi, "-").slice(0, 40);
  const blob = new Blob([JSON.stringify(credentialExportPayload(d), null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `credential-${safeLabel}.json`;
  a.click();
  URL.revokeObjectURL(url);
}

function SecretBlock({
  label,
  value,
  isLight,
}: {
  label: string;
  value: string;
  isLight: boolean;
}) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    if (!value) return;
    void navigator.clipboard.writeText(value).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span
          className={`text-[10px] font-mono uppercase tracking-wider ${isLight ? "text-neutral-500" : "text-gray-500"}`}
        >
          {label}
        </span>
        <button
          type="button"
          onClick={copy}
          disabled={!value}
          className={`text-xs flex items-center gap-1 px-2 py-0.5 rounded border transition-colors ${
            isLight
              ? "border-neutral-200 text-neutral-600 hover:bg-neutral-50"
              : "border-neutral-600 text-gray-300 hover:bg-neutral-800"
          } disabled:opacity-40`}
        >
          <Copy className="w-3 h-3" />
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <div
        className={`font-mono text-xs break-all rounded p-2 border ${
          isLight ? "bg-neutral-50 border-neutral-200 text-neutral-900" : "bg-neutral-950 border-neutral-700 text-green-400"
        }`}
      >
        {value || "—"}
      </div>
    </div>
  );
}

export default function ResourceAccess() {
  const { theme } = useTheme();
  const isLight = theme === "white";

  const [credentials, setCredentials] = useState<ResourceAccessCredentialListItem[]>([]);
  const [groups, setGroups] = useState<ResourceAccessGroup[]>([]);
  const [loadingList, setLoadingList] = useState(true);
  const [listError, setListError] = useState<string | null>(null);

  const [showCreate, setShowCreate] = useState(false);
  const [editingResourceAccessId, setEditingResourceAccessId] = useState<string | null>(null);
  const [loadingEditDetail, setLoadingEditDetail] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [resourceType, setResourceType] = useState<ApiResourceType>("DataModel");
  const [label, setLabel] = useState("");
  const [description, setDescription] = useState("");
  const [resourceGroupId, setResourceGroupId] = useState("");
  const [selGuids, setSelGuids] = useState<Set<string>>(new Set());
  const [resFilter, setResFilter] = useState("");

  const [dataModels, setDataModels] = useState<DataModelCatalogRow[]>([]);
  const [knowledgeBases, setKnowledgeBases] = useState<KnowledgeBaseListItem[]>([]);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [optionsError, setOptionsError] = useState<string | null>(null);

  const [showGroupsModal, setShowGroupsModal] = useState(false);
  const [rotateState, setRotateState] = useState<{
    credentialId: string;
    label: string;
    detail: ResourceAccessCredentialDetail | null;
    loading: boolean;
    error: string | null;
  } | null>(null);

  const [deleteConfirm, setDeleteConfirm] = useState<{ id: string; label: string } | null>(null);
  const [revokeConfirm, setRevokeConfirm] = useState<{ id: string; label: string } | null>(null);
  const [actionId, setActionId] = useState<string | null>(null);

  /** Read-only credential details (secrets when API returns them). */
  const [viewModal, setViewModal] = useState<{
    loading: boolean;
    error: string | null;
    detail: ResourceAccessCredentialDetail | null;
  } | null>(null);

  const [showB2bApiHelp, setShowB2bApiHelp] = useState(false);
  const [b2bApiHelpTab, setB2bApiHelpTab] = useState<"csharp" | "python" | "java" | "nodejs" | "curl">("csharp");

  const groupNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const g of groups) {
      if (g.resourceGroupId) m.set(g.resourceGroupId, g.resourceGroupName || g.resourceGroupId);
      if (g.id) m.set(g.id, g.resourceGroupName || g.id);
    }
    return m;
  }, [groups]);

  const loadGroups = useCallback(async () => {
    try {
      const list = await resourceAccessService.listGroups();
      setGroups(list);
    } catch {
      setGroups([]);
    }
  }, []);

  const loadCredentials = useCallback(async (opts?: { silent?: boolean }) => {
    setListError(null);
    if (!opts?.silent) {
      setLoadingList(true);
    }
    try {
      const list = await resourceAccessService.listCredentials();
      setCredentials(list);
    } catch (e) {
      setListError(e instanceof Error ? e.message : "Failed to load credentials");
      setCredentials([]);
    } finally {
      if (!opts?.silent) {
        setLoadingList(false);
      }
    }
  }, []);

  useEffect(() => {
    void loadCredentials();
    void loadGroups();
  }, [loadCredentials, loadGroups]);

  useEffect(() => {
    if (!showCreate) return;
    let cancelled = false;
    setOptionsLoading(true);
    setOptionsError(null);
    (async () => {
      try {
        if (resourceType === "DataModel") {
          const list = await dataModelsCatalogService.list();
          if (!cancelled) {
            setDataModels(
              list.filter((d) => {
                const s = (d.status || "").toLowerCase();
                return !s || s === "active";
              })
            );
            setKnowledgeBases([]);
          }
        } else {
          const list = await featureHelperService.listKnowledgeBases();
          if (!cancelled) {
            setKnowledgeBases(list);
            setDataModels([]);
          }
        }
      } catch (e) {
        if (!cancelled) {
          setDataModels([]);
          setKnowledgeBases([]);
          setOptionsError(
            e instanceof Error
              ? e.message
              : resourceType === "DataModel"
                ? "Failed to load data models"
                : "Failed to load knowledge bases"
          );
        }
      } finally {
        if (!cancelled) setOptionsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showCreate, resourceType]);

  const resourceRows = useMemo(() => {
    if (resourceType === "DataModel") {
      return dataModels.map((d) => {
        const selUuid = String(d.uuid || d.id).trim();
        const hasMappableGuid = isResourceAccessResourceId(selUuid);
        return {
          rowKey: `dm-${d.id}`,
          guid: selUuid,
          displayId: d.id,
          hasMappableGuid,
          title: d.modelName,
          subtitle: d.description || d.type || "—",
          tag: "Data model",
        };
      });
    }
    return knowledgeBases.map((k) => {
      const guid = String(k.resourceAccessGuid || "").trim();
      const hasMappableGuid = isResourceAccessResourceId(guid);
      return {
        rowKey: `kb-${k.id}`,
        guid,
        displayId: k.id,
        hasMappableGuid,
        title: k.title,
        subtitle: k.description || k.collectionNamespace || "—",
        tag: "Knowledge base",
      };
    });
  }, [resourceType, dataModels, knowledgeBases]);

  const filteredRows = useMemo(
    () =>
      resourceRows.filter((r) =>
        (r.guid + r.displayId + r.title + r.subtitle).toLowerCase().includes(resFilter.toLowerCase())
      ),
    [resourceRows, resFilter]
  );

  const isCreateFormComplete = useMemo(
    () =>
      label.trim() !== "" &&
      resourceGroupId.trim() !== "" &&
      isRFC4122Guid(resourceGroupId.trim()) &&
      selGuids.size > 0,
    [label, resourceGroupId, selGuids]
  );

  const toggleGuid = (guid: string, enabled: boolean) => {
    if (!enabled || !guid) return;
    setSelGuids((prev) => {
      const n = new Set(prev);
      if (n.has(guid)) n.delete(guid);
      else n.add(guid);
      return n;
    });
  };

  const resetCreateForm = () => {
    setShowCreate(false);
    setEditingResourceAccessId(null);
    setLoadingEditDetail(false);
    setFormError(null);
    setLabel("");
    setDescription("");
    setResourceGroupId("");
    setSelGuids(new Set());
    setResourceType("DataModel");
    setResFilter("");
  };

  const openEditCredential = async (row: ResourceAccessCredentialListItem) => {
    const applicationId = (row.applicationId || row.resourceGroupId || "").trim();
    if (!applicationId) {
      setFormError("Application id is missing for this credential.");
      return;
    }
    setEditingResourceAccessId(row.id);
    setShowCreate(true);
    setFormError(null);
    setLoadingEditDetail(true);
    try {
      const detail = await resourceAccessService.getCredentialByApplicationId(applicationId);
      setResourceType(detail.resourceType);
      setLabel(detail.label);
      setDescription(detail.description);
      setResourceGroupId(detail.resourceGroupId || applicationId);
      setSelGuids(new Set(detail.resourceGuids.filter(isResourceAccessResourceId)));
      setResFilter("");
    } catch (e) {
      setFormError(e instanceof Error ? e.message : "Failed to load credential");
    } finally {
      setLoadingEditDetail(false);
    }
  };

  const submitCreate = async () => {
    if (submitting) return;
    const trimmedLabel = label.trim();
    if (!trimmedLabel) {
      setFormError("Label is required.");
      return;
    }
    if (!resourceGroupId.trim()) {
      setFormError("Select an application.");
      return;
    }
    if (!isRFC4122Guid(resourceGroupId)) {
      setFormError("Application id must be a valid GUID. Re-open application management or pick another application.");
      return;
    }
    const guids = Array.from(selGuids).filter(isResourceAccessResourceId);
    if (guids.length === 0) {
      setFormError(
        "Select at least one resource with a valid id (data model `uuid` or knowledge base id)."
      );
      return;
    }
    setSubmitting(true);
    setFormError(null);
    const applicationId = resourceGroupId.trim();
    const payload = {
      resourceType,
      resourceGuids: guids,
      label: trimmedLabel,
      description: description.trim(),
    };
    try {
      if (editingResourceAccessId) {
        await resourceAccessService.updateResourceAccess(editingResourceAccessId, payload);
      } else {
        await resourceAccessService.createApplicationResourceAccess(applicationId, payload);
      }
      resetCreateForm();
      void loadCredentials({ silent: true });
    } catch (e) {
      setFormError(
        e instanceof Error
          ? e.message
          : "Failed to save changes"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const applyDelete = async () => {
    if (!deleteConfirm) return;
    setActionId(deleteConfirm.id);
    try {
      await resourceAccessService.deleteCredential(deleteConfirm.id);
      setDeleteConfirm(null);
      void loadCredentials({ silent: true });
    } catch (e) {
      setListError(e instanceof Error ? e.message : "Delete failed");
    } finally {
      setActionId(null);
    }
  };

  const applyRevoke = async () => {
    if (!revokeConfirm) return;
    setActionId(revokeConfirm.id);
    try {
      await resourceAccessService.revokeCredential(revokeConfirm.id);
      setRevokeConfirm(null);
      void loadCredentials({ silent: true });
    } catch (e) {
      setListError(e instanceof Error ? e.message : "Revoke failed");
    } finally {
      setActionId(null);
    }
  };

  const openRotate = (row: ResourceAccessCredentialListItem) => {
    setRotateState({ credentialId: row.id, label: row.label, detail: null, loading: false, error: null });
  };

  const runRotate = async () => {
    if (!rotateState || rotateState.loading) return;
    setRotateState((s) => (s ? { ...s, loading: true, error: null } : s));
    try {
      const detail = await resourceAccessService.rotateSecret(rotateState.credentialId);
      setRotateState((s) => (s ? { ...s, detail, loading: false } : s));
      void loadCredentials({ silent: true });
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Reset credentials failed";
      setRotateState((s) => (s ? { ...s, loading: false, error: msg } : s));
    }
  };

  const openViewCredentialByApplicationId = async (applicationId: string) => {
    const routeId = String(applicationId ?? "").trim();
    if (!routeId) return;
    setViewModal({ loading: true, error: null, detail: null });
    try {
      const detail = await resourceAccessService.getCredentialByApplicationId(routeId);
      setViewModal({ loading: false, error: null, detail });
    } catch (e) {
      setViewModal({
        loading: false,
        error: e instanceof Error ? e.message : "Failed to load credential",
        detail: null,
      });
    }
  };

  const panelClass = isLight
    ? "bg-white border-neutral-200 text-neutral-900"
    : "bg-neutral-900 border-neutral-800 text-gray-200";
  const headerBar = isLight ? "bg-neutral-50 border-neutral-200" : "bg-neutral-900 border-neutral-800";
  const inputClass = isLight
    ? "bg-white border-neutral-300 text-neutral-900 shadow-sm"
    : "bg-neutral-800 border-neutral-700 text-gray-200";

  const b2bUrlLiteral = "{URL}api/b2bdm/rs?resourceGroup={GroupId}";

  return (
    <div className="portal-content p-6 space-y-6 text-sm text-gray-300">
      {listError && (
        <div className="rounded border border-red-800 bg-red-900/30 px-4 py-2 text-xs text-red-200">{listError}</div>
      )}

      <div className="flex justify-between items-center flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-semibold text-white">Resource access</h2>
          <p className="text-sm text-gray-400">
            Map resources to an application, edit resource access (without rotating tokens), reset credentials (new secrets), or
            revoke access. Use <span className="font-medium text-gray-300">Manage Applications</span> to view client ID,
            secret, and token for each credential.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowB2bApiHelp(true)}
            title="B2B API examples"
            aria-label="B2B API examples"
            className={`inline-flex items-center justify-center p-2.5 rounded border transition-colors ${
              isLight
                ? "bg-white border-neutral-300 text-neutral-800 hover:bg-neutral-50"
                : "bg-neutral-800 border-neutral-600 text-gray-200 hover:bg-neutral-700"
            }`}
          >
            <BookOpen className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => {
              setEditingResourceAccessId(null);
              setLoadingEditDetail(false);
              setShowCreate(true);
              setFormError(null);
            }}
            className="flex items-center bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded text-sm shadow transition-colors"
          >
            <PlusCircle className="w-4 h-4 mr-2" />
            Map Resources
          </button>
          <button
            type="button"
            onClick={() => {
              void loadCredentials({ silent: true });
              setShowGroupsModal(true);
            }}
            title="Manage Applications"
            className={`flex items-center px-4 py-2 rounded text-sm border shadow transition-colors ${
              isLight
                ? "bg-white border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                : "bg-neutral-800 border-indigo-700 text-indigo-300 hover:bg-indigo-950/50"
            }`}
          >
            <SquarePlus className="w-4 h-4 mr-2" />
            Manage Applications
          </button>
        </div>
      </div>

      {showCreate &&
        createPortal(
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-md">
            <div className={`border rounded-lg shadow-xl w-full max-w-[50vw] max-h-[90vh] overflow-hidden flex flex-col ${panelClass}`}>
              <div className={`flex items-center justify-between px-4 py-3 border-b flex-shrink-0 ${headerBar}`}>
                <div>
                  <h3 className={`font-semibold text-sm ${isLight ? "text-neutral-900" : "text-white"}`}>
                    {editingResourceAccessId ? "Edit resource access" : "New resource access"}
                  </h3>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {editingResourceAccessId
                      ? "Update resource type, label, description, and mapped resources. Tokens stay the same — use Reset credentials in the list to rotate secrets."
                      : "Select resource type, label, description, application, and at least one resource. Client ID and secrets are available under Manage Applications → View credentials."}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={resetCreateForm}
                  className="p-1.5 rounded text-gray-400 hover:text-white hover:bg-neutral-800 transition-colors"
                  aria-label="Close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-5 overflow-y-auto flex-1 space-y-5 relative">
                  {loadingEditDetail && (
                    <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/30 rounded">
                      <Loader2 className="w-8 h-8 animate-spin text-indigo-400" />
                    </div>
                  )}
                  {formError && (
                    <div className="rounded border border-red-800 bg-red-900/20 px-3 py-2 text-xs text-red-200">
                      {formError}
                    </div>
                  )}

                  <div>
                    <p className={`text-xs font-medium mb-2 ${isLight ? "text-neutral-500" : "text-gray-400"}`}>
                      Resource type
                    </p>
                    <select
                      value={resourceType}
                      disabled={!!editingResourceAccessId}
                      onChange={(e) => {
                        const v = e.target.value as ApiResourceType;
                        setResourceType(v);
                        setSelGuids(new Set());
                        setResFilter("");
                      }}
                      className={`w-full rounded px-3 py-2 text-xs outline-none focus:ring-1 focus:ring-indigo-500 disabled:opacity-60 ${inputClass}`}
                    >
                      <option value="DataModel">Data model</option>
                      <option value="KnowledgeBase">Knowledge base</option>
                    </select>
                  </div>

                  <div>
                    <p className={`text-xs font-medium mb-2 ${isLight ? "text-neutral-500" : "text-gray-400"}`}>
                      Label
                    </p>
                    <input
                      className={`w-full rounded px-3 py-2 text-xs placeholder:text-gray-500 outline-none focus:border-indigo-500 ${inputClass}`}
                      placeholder="e.g. Partner integration"
                      value={label}
                      onChange={(e) => setLabel(e.target.value)}
                    />
                  </div>

                  <div>
                    <p className={`text-xs font-medium mb-2 ${isLight ? "text-neutral-500" : "text-gray-400"}`}>
                      Description
                    </p>
                    <textarea
                      className={`w-full rounded px-3 py-2 text-xs placeholder:text-gray-400 outline-none focus:ring-1 focus:ring-indigo-500 resize-none ${inputClass}`}
                      rows={2}
                      placeholder="What this access is for…"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                    />
                  </div>

                  <div>
                    <p className={`text-xs font-medium mb-2 ${isLight ? "text-neutral-500" : "text-gray-400"}`}>
                      Application
                    </p>
                    <select
                      value={resourceGroupId}
                      disabled={!!editingResourceAccessId}
                      onChange={(e) => setResourceGroupId(e.target.value)}
                      className={`w-full rounded px-3 py-2 text-xs outline-none focus:ring-1 focus:ring-indigo-500 disabled:opacity-60 ${inputClass}`}
                    >
                      <option value="">Select Application</option>
                      {groups.map((g) => (
                        <option key={g.id || g.resourceGroupId} value={g.resourceGroupId || g.id}>
                          {g.resourceGroupName || g.resourceGroupId}
                          {!g.isActive ? " (inactive)" : ""}
                        </option>
                      ))}
                    </select>
                  </div>


                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <p className={`text-xs font-medium ${isLight ? "text-neutral-500" : "text-gray-400"}`}>
                        Resources
                      </p>
                      <span
                        className={`inline-block px-2 py-0.5 rounded text-xs font-medium border ${
                          isLight
                            ? "bg-indigo-50 text-indigo-600 border-indigo-100"
                            : "bg-indigo-900/50 text-indigo-300 border-indigo-700/30"
                        }`}
                      >
                        {selGuids.size} selected
                      </span>
                    </div>
                    {optionsLoading ? (
                      <div className="flex items-center gap-2 text-xs text-gray-500 py-6 justify-center">
                        <Loader2 className="w-4 h-4 animate-spin" /> Loading…
                      </div>
                    ) : optionsError ? (
                      <div className="text-xs text-red-400 py-2">{optionsError}</div>
                    ) : (
                      <>
                        <div className="flex gap-2 mb-2">
                          <button
                            type="button"
                            onClick={() =>
                              setSelGuids(new Set(resourceRows.filter((r) => r.hasMappableGuid && r.guid).map((r) => r.guid)))
                            }
                            className={`text-xs border rounded px-2 py-1 transition-colors ${
                              isLight
                                ? "text-neutral-600 bg-white border-neutral-300"
                                : "text-gray-400 border-neutral-700 hover:bg-neutral-800"
                            }`}
                          >
                            Select all
                          </button>
                          <button
                            type="button"
                            onClick={() => setSelGuids(new Set())}
                            className={`text-xs border rounded px-2 py-1 transition-colors ${
                              isLight
                                ? "text-neutral-600 bg-white border-neutral-300"
                                : "text-gray-400 border-neutral-700 hover:bg-neutral-800"
                            }`}
                          >
                            Clear
                          </button>
                          <input
                            className={`flex-1 border rounded px-2 py-1 font-mono text-xs placeholder:text-gray-400 outline-none focus:ring-1 focus:ring-indigo-500 ${inputClass}`}
                            placeholder="Filter…"
                            value={resFilter}
                            onChange={(e) => setResFilter(e.target.value)}
                          />
                        </div>
                        <div
                          className={`space-y-2 max-h-64 overflow-y-auto pr-1 ${
                            isLight ? "scrollbar-thin scrollbar-thumb-neutral-300" : "scrollbar-thin scrollbar-thumb-neutral-700"
                          }`}
                        >
                          {filteredRows.map((r) => {
                            const sel = r.hasMappableGuid && r.guid.length > 0 && selGuids.has(r.guid);
                            return (
                              <button
                                type="button"
                                key={r.rowKey}
                                disabled={!r.hasMappableGuid}
                                onClick={() => toggleGuid(r.guid, r.hasMappableGuid)}
                                className={`w-full text-left flex items-start gap-3 p-3 rounded-lg border transition-all ${
                                  !r.hasMappableGuid
                                    ? isLight
                                      ? "border-neutral-100 bg-neutral-50/80 opacity-70 cursor-not-allowed"
                                      : "border-neutral-800 bg-neutral-950/40 opacity-60 cursor-not-allowed"
                                    : sel
                                      ? isLight
                                        ? "border-indigo-400 bg-indigo-50/80 ring-1 ring-indigo-100 cursor-pointer"
                                        : "border-indigo-500/50 bg-indigo-500/10 ring-1 ring-indigo-500/20 cursor-pointer"
                                      : isLight
                                        ? "border-neutral-200 bg-white hover:border-neutral-300 cursor-pointer"
                                        : "border-neutral-700 bg-neutral-900/30 hover:border-neutral-600 cursor-pointer"
                                }`}
                              >
                                <div
                                  className={`mt-0.5 w-4 h-4 rounded flex items-center justify-center text-[9px] flex-shrink-0 border ${
                                    sel
                                      ? "bg-indigo-600 border-indigo-500 text-white font-bold"
                                      : isLight
                                        ? "border-neutral-400 bg-white"
                                        : "border-neutral-500 bg-neutral-900"
                                  }`}
                                >
                                  {sel ? "✓" : ""}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-start justify-between gap-2">
                                    <p
                                      className={`font-mono text-xs leading-5 ${
                                        sel
                                          ? isLight
                                            ? "text-indigo-700"
                                            : "text-indigo-300"
                                          : isLight
                                            ? "text-neutral-900"
                                            : "text-neutral-100"
                                      }`}
                                    >
                                      {r.guid || r.displayId}
                                    </p>
                                    <span
                                      className={`text-[10px] px-2 py-0.5 rounded-full border whitespace-nowrap ${
                                        isLight
                                          ? "bg-neutral-100 text-neutral-600 border-neutral-200"
                                          : "bg-neutral-800 text-neutral-300 border-neutral-700"
                                      }`}
                                    >
                                      {r.tag}
                                    </span>
                                  </div>
                                  <p className={`text-[11px] mt-0.5 leading-4 ${isLight ? "text-neutral-600" : "text-neutral-400"}`}>
                                    {r.title}
                                  </p>
                                  <p className={`text-[10px] mt-0.5 ${isLight ? "text-neutral-500" : "text-neutral-500"}`}>
                                    {r.subtitle}
                                  </p>
                                  {!r.hasMappableGuid && (
                                    <p className="text-[10px] mt-1 text-amber-600/90">
                                      No usable resource id on this row (expect `uuid` from /api/DataModels or a
                                      supported knowledge-base id).
                                    </p>
                                  )}
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </>
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={() => void submitCreate()}
                    disabled={submitting || loadingEditDetail || !isCreateFormComplete}
                    className={`flex items-center justify-center w-full py-2.5 rounded text-sm font-medium shadow transition-colors ${
                      submitting ? "bg-neutral-800 text-neutral-500 cursor-wait" : "bg-indigo-600 hover:bg-indigo-700 text-white"
                    }`}
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Saving…
                      </>
                    ) : (
                      <>
                        <Pencil className="w-4 h-4 mr-2" />
                        Save changes
                      </>
                    )}
                  </button>
                </div>
            </div>
          </div>,
          document.body
        )}

      {showGroupsModal &&
        createPortal(
          <ResourceGroupsModal
            isLight={isLight}
            credentials={credentials}
            onViewApplicationCredentials={(applicationId) => void openViewCredentialByApplicationId(applicationId)}
            onClose={() => {
              setShowGroupsModal(false);
              void loadGroups();
            }}
          />,
          document.body
        )}

      {rotateState &&
        createPortal(
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <div className={`border rounded-lg shadow-xl max-w-md w-full p-5 ${panelClass}`}>
              <div className="flex items-start justify-between gap-2 mb-3">
                <div>
                  <h4 className={`text-sm font-semibold ${isLight ? "text-neutral-900" : "text-white"}`}>
                    Reset credentials
                  </h4>
                  <p className={`text-xs mt-1 ${isLight ? "text-neutral-600" : "text-gray-400"}`}>{rotateState.label}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setRotateState(null)}
                  className="p-1 rounded text-gray-400 hover:text-white hover:bg-neutral-800"
                  aria-label="Close"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              {rotateState.error && <p className="text-xs text-red-400 mb-2">{rotateState.error}</p>}
              {!rotateState.detail ? (
                <div className="space-y-3">
                  <p className={`text-xs ${isLight ? "text-neutral-600" : "text-gray-400"}`}>
                    Issues new client ID, client secret, and access token at the server. This is separate from
                    <span className="font-medium"> Revoke</span>, which invalidates access without issuing new secrets
                    here.
                  </p>
                  <div className="flex gap-2 justify-end">
                    <button
                      type="button"
                      onClick={() => setRotateState(null)}
                      className={`px-3 py-1.5 rounded text-xs font-medium border ${
                        isLight
                          ? "border-neutral-300 bg-white text-neutral-600"
                          : "border-neutral-600 bg-neutral-800 text-gray-300"
                      }`}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      disabled={rotateState.loading}
                      onClick={() => void runRotate()}
                      className="px-3 py-1.5 rounded text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 inline-flex items-center gap-1 disabled:opacity-60"
                    >
                      {rotateState.loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                      Reset credentials
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <SecretBlock label="Client ID" value={rotateState.detail.clientId} isLight={isLight} />
                  <SecretBlock label="Client secret" value={rotateState.detail.clientSecret} isLight={isLight} />
                  <SecretBlock label="Access token" value={rotateState.detail.accessToken} isLight={isLight} />
                  <button
                    type="button"
                    onClick={() => setRotateState(null)}
                    className="w-full py-2 rounded text-xs border border-neutral-600 text-gray-300 hover:bg-neutral-800"
                  >
                    Close
                  </button>
                </div>
              )}
            </div>
          </div>,
          document.body
        )}

      <div className="overflow-x-auto border border-neutral-800 rounded">
        <table className="min-w-full text-sm text-left text-gray-300">
          <thead className="bg-neutral-900 text-gray-400">
            <tr>
              {["Label", "Type", "Application", "Client ID", "Status", "Created", "Actions"].map((h) => (
                <th key={h} className="px-4 py-2 text-xs font-medium whitespace-nowrap">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loadingList ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-gray-500 text-xs">
                  <Loader2 className="w-5 h-5 inline animate-spin mr-2 align-middle" />
                  Loading credentials…
                </td>
              </tr>
            ) : credentials.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-gray-500 text-xs">
                  No credentials yet. Create one to get started.
                </td>
              </tr>
            ) : (
              credentials.map((row, i) => {
                const revoked = statusLooksRevoked(row.status);
                const gName =
                  groupNameById.get(row.resourceGroupId) ||
                  (row.resourceGroupId ? row.resourceGroupId.slice(0, 8) + "…" : "—");
                return (
                  <tr key={row.id} className="border-t border-neutral-800 hover:bg-neutral-800/30">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div
                          className={`w-6 h-6 rounded bg-gradient-to-br ${GRADIENTS[i % GRADIENTS.length]} flex items-center justify-center text-[9px] font-bold text-white flex-shrink-0`}
                        >
                          {initFromName(row.label || row.resourceTypeName || "?")}
                        </div>
                        <span className="text-gray-200">{row.label}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-400 whitespace-nowrap">
                      {row.resourceTypeName || row.resourceType}
                    </td>
                    <td className="px-4 py-3 text-gray-400 max-w-[140px] truncate" title={gName}>
                      {gName}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-gray-500 max-w-[160px] truncate" title={row.clientId}>
                      {row.clientId || "—"}
                    </td>
                    <td className="px-4 py-3 text-xs">{row.status || "—"}</td>
                    <td className="px-4 py-3 font-mono text-xs text-gray-500 whitespace-nowrap">
                      {row.createdOn ? new Date(row.createdOn).toLocaleString() : "—"}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          type="button"
                          title="Edit resource access (metadata & resources — does not change tokens)"
                          disabled={
                            actionId === row.id ||
                            (loadingEditDetail &&
                              editingResourceAccessId === row.id)
                          }
                          onClick={() => void openEditCredential(row)}
                          className="text-amber-400 hover:text-amber-300 disabled:opacity-40"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        {/* <button
                          type="button"
                          title="Reset credentials (new secrets at server)"
                          disabled={actionId === row.id}
                          onClick={() => openRotate(row)}
                          className="text-indigo-400 hover:text-indigo-300 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          <RefreshCw className="w-4 h-4" />
                        </button> */}
                        {/* {revoked ? (
                          <span
                            className={`text-[10px] px-2 py-1 rounded border uppercase tracking-wide ${
                              isLight ? "border-neutral-300 text-neutral-500 bg-neutral-100" : "border-neutral-600 text-gray-500 bg-neutral-800/80"
                            }`}
                            title="Revoked — access invalid. Use Reset credentials for new secrets, or delete this credential."
                          >
                            Revoked
                          </span>
                        ) : (
                          <button
                            type="button"
                            disabled={actionId === row.id}
                            onClick={() => setRevokeConfirm({ id: row.id, label: row.label })}
                            className="relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60 bg-green-600"
                            role="switch"
                            aria-checked
                            title="Revoke"
                          >
                            <span
                              className="pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition duration-200 ease-in-out translate-x-5"
                              style={{ marginTop: 2 }}
                            />
                          </button>
                        )} */}
                        <button
                          type="button"
                          title="Delete"
                          disabled={actionId === row.id}
                          onClick={() => setDeleteConfirm({ id: row.id, label: row.label })}
                          className="text-red-400 hover:text-red-200 disabled:opacity-40"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {revokeConfirm &&
        createPortal(
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <div className={`${panelClass} border rounded-lg shadow-xl max-w-sm w-full p-5`}>
              <h4 className={`text-sm font-semibold mb-1 ${isLight ? "text-neutral-900" : "text-white"}`}>Revoke access</h4>
              <p className={`text-xs mb-4 ${isLight ? "text-neutral-600" : "text-gray-400"}`}>
                Revoke credentials for <span className="text-gray-200 font-medium">&quot;{revokeConfirm.label}&quot;</span>?
              </p>
              <div className="flex gap-2 justify-end">
                <button
                  type="button"
                  onClick={() => setRevokeConfirm(null)}
                  className={`px-3 py-1.5 rounded text-xs font-medium border ${
                    isLight ? "border-neutral-300 bg-white text-neutral-600" : "border-neutral-600 bg-neutral-800 text-gray-300"
                  }`}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => void applyRevoke()}
                  className="px-3 py-1.5 rounded text-xs font-medium text-white bg-red-600 hover:bg-red-700"
                >
                  Revoke
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {deleteConfirm &&
        createPortal(
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <div className={`${panelClass} border rounded-lg shadow-xl max-w-sm w-full p-5`}>
              <h4 className={`text-sm font-semibold mb-1 ${isLight ? "text-neutral-900" : "text-white"}`}>Delete credentials</h4>
              <p className={`text-xs mb-4 ${isLight ? "text-neutral-600" : "text-gray-400"}`}>
                Permanently delete <span className="text-gray-200 font-medium">&quot;{deleteConfirm.label}&quot;</span>? This
                cannot be undone.
              </p>
              <div className="flex gap-2 justify-end">
                <button
                  type="button"
                  onClick={() => setDeleteConfirm(null)}
                  className={`px-3 py-1.5 rounded text-xs font-medium border ${
                    isLight ? "border-neutral-300 bg-white text-neutral-600" : "border-neutral-600 bg-neutral-800 text-gray-300"
                  }`}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => void applyDelete()}
                  className="px-3 py-1.5 rounded text-xs font-medium text-white bg-red-600 hover:bg-red-700"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {viewModal !== null &&
        createPortal(
          <div className="fixed inset-0 z-[130] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <div className={`${panelClass} border rounded-lg shadow-xl max-w-xl w-full max-h-[85vh] overflow-hidden flex flex-col`}>
              <div className={`flex items-center justify-between gap-2 px-4 py-3 border-b flex-shrink-0 ${headerBar}`}>
                <div className="min-w-0">
                  <h4 className={`text-sm font-semibold truncate ${isLight ? "text-neutral-900" : "text-white"}`}>
                    View credentials
                  </h4>
                  {viewModal.detail?.label ? (
                    <p className={`text-[10px] truncate ${isLight ? "text-neutral-500" : "text-gray-500"}`}>
                      {viewModal.detail.label}
                    </p>
                  ) : null}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {viewModal.detail ? (
                    <button
                      type="button"
                      onClick={() => downloadCredentialJson(viewModal.detail!)}
                      className={`flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium border transition-colors ${
                        isLight
                          ? "border-neutral-200 text-neutral-600 hover:bg-neutral-50"
                          : "border-neutral-600 text-gray-300 hover:bg-neutral-800"
                      }`}
                    >
                      <Download className="w-3 h-3" />
                      JSON
                    </button>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => setViewModal(null)}
                    className="p-1.5 rounded text-gray-400 hover:text-white hover:bg-neutral-800 transition-colors"
                    aria-label="Close"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
              <div className="p-4 overflow-y-auto flex-1 space-y-4 text-xs">
                {viewModal.loading ? (
                  <div className="flex justify-center py-10 text-gray-500">
                    <Loader2 className="w-8 h-8 animate-spin text-indigo-400" />
                  </div>
                ) : viewModal.error ? (
                  <div className="text-red-400 border border-red-800/50 rounded px-3 py-2">{viewModal.error}</div>
                ) : viewModal.detail ? (
                  <>
                    <div
                      className={`rounded-lg border p-3 grid grid-cols-2 gap-x-4 gap-y-2.5 ${
                        isLight ? "border-neutral-200 bg-neutral-50" : "border-neutral-700 bg-neutral-950/50"
                      }`}
                    >
                      <div className="col-span-2 flex items-center gap-2 flex-wrap">
                        <span
                          className={`text-[10px] font-mono uppercase tracking-wider ${isLight ? "text-neutral-500" : "text-gray-500"}`}
                        >
                          Status
                        </span>
                        <span
                          className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                            statusLooksRevoked(viewModal.detail.status) || !viewModal.detail.isActive
                              ? isLight
                                ? "bg-red-100 text-red-700"
                                : "bg-red-950/60 text-red-400 border border-red-900/50"
                              : isLight
                                ? "bg-emerald-100 text-emerald-800"
                                : "bg-emerald-950/50 text-emerald-400 border border-emerald-900/40"
                          }`}
                        >
                          {viewModal.detail.status || (viewModal.detail.isActive ? "Active" : "Inactive")}
                        </span>
                      </div>
                      <div>
                        <p
                          className={`text-[10px] font-mono uppercase tracking-wider mb-0.5 ${isLight ? "text-neutral-500" : "text-gray-500"}`}
                        >
                          Expiry
                        </p>
                        <p className={`font-medium ${isLight ? "text-neutral-900" : "text-gray-200"}`}>
                          {viewModal.detail.expiry?.trim() || "—"}
                        </p>
                      </div>
                      <div>
                        <p
                          className={`text-[10px] font-mono uppercase tracking-wider mb-0.5 ${isLight ? "text-neutral-500" : "text-gray-500"}`}
                        >
                          Rate limit
                        </p>
                        <p className={`font-medium ${isLight ? "text-neutral-900" : "text-gray-200"}`}>
                          {typeof viewModal.detail.rateLimitPerMinute === "number"
                            ? `${viewModal.detail.rateLimitPerMinute} / min`
                            : "—"}
                        </p>
                      </div>
                      <div className="col-span-2">
                        <p
                          className={`text-[10px] font-mono uppercase tracking-wider mb-0.5 ${isLight ? "text-neutral-500" : "text-gray-500"}`}
                        >
                          Expires at
                        </p>
                        <p className={`font-medium ${isLight ? "text-neutral-900" : "text-gray-200"}`}>
                          {formatApiDate(viewModal.detail.expiresAt)}
                        </p>
                      </div>
                    </div>
                    <SecretBlock label="Client ID" value={viewModal.detail.clientId} isLight={isLight} />
                    <SecretBlock label="Client secret" value={viewModal.detail.clientSecret} isLight={isLight} />
                    <SecretBlock label="Access token" value={viewModal.detail.accessToken} isLight={isLight} />
                  </>
                ) : null}
              </div>
            </div>
          </div>,
          document.body
        )}

      {showB2bApiHelp &&
        createPortal(
          <div className="fixed inset-0 z-[106] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <div className={`${panelClass} border rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col`}>
              <div className={`flex items-center justify-between px-4 py-3 border-b flex-shrink-0 ${headerBar}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <BookOpen className={`w-4 h-4 flex-shrink-0 ${isLight ? "text-neutral-600" : "text-gray-400"}`} aria-hidden />
                  <div>
                    <h4 className={`text-sm font-semibold ${isLight ? "text-neutral-900" : "text-white"}`}>B2B API</h4>
                    <p className={`text-[10px] ${isLight ? "text-neutral-500" : "text-gray-500"}`}>
                      GET · <span className="font-mono">X-B2B-Client-Id</span> · <span className="font-mono">X-B2B-Client-Secret</span>
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setShowB2bApiHelp(false)}
                  className="p-1.5 rounded text-gray-400 hover:text-white hover:bg-neutral-800 transition-colors"
                  aria-label="Close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="px-4 pt-3 flex flex-wrap gap-1 border-b border-neutral-700/50 flex-shrink-0">
                {(
                  [
                    ["csharp", "C#"],
                    ["python", "Python"],
                    ["java", "Java"],
                    ["nodejs", "Node.js"],
                    ["curl", "cURL"],
                  ] as const
                ).map(([id, label]) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setB2bApiHelpTab(id)}
                    className={`px-2.5 py-1.5 rounded text-xs font-medium transition-colors ${
                      b2bApiHelpTab === id
                        ? isLight
                          ? "bg-indigo-100 text-indigo-800 border border-indigo-200"
                          : "bg-indigo-900/40 text-indigo-200 border border-indigo-700/50"
                        : isLight
                          ? "text-neutral-600 hover:bg-neutral-100 border border-transparent"
                          : "text-gray-400 hover:bg-neutral-800 border border-transparent"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <div className="p-4 overflow-y-auto flex-1">
                <pre
                  className={`font-mono text-[11px] leading-relaxed whitespace-pre-wrap break-all rounded p-3 border overflow-x-auto ${
                    isLight ? "bg-neutral-50 border-neutral-200 text-neutral-900" : "bg-neutral-950 border-neutral-700 text-gray-300"
                  }`}
                >
                  {b2bApiHelpTab === "csharp" &&
                    `var client = new HttpClient();
var request = new HttpRequestMessage(HttpMethod.Get, "${b2bUrlLiteral}");
request.Headers.Add("X-B2B-Client-Id", "{client_id}");
request.Headers.Add("X-B2B-Client-Secret", "{client_secret}");
var response = await client.SendAsync(request);
response.EnsureSuccessStatusCode();
Console.WriteLine(await response.Content.ReadAsStringAsync());`}
                  {b2bApiHelpTab === "python" &&
                    `import urllib.request

url = "${b2bUrlLiteral}"
req = urllib.request.Request(url)
req.add_header("X-B2B-Client-Id", "{client_id}")
req.add_header("X-B2B-Client-Secret", "{client_secret}")
with urllib.request.urlopen(req) as resp:
    print(resp.read().decode())`}
                  {b2bApiHelpTab === "java" &&
                    `import java.net.URI;
import java.net.http.HttpClient;
import java.net.http.HttpRequest;
import java.net.http.HttpResponse;

var client = HttpClient.newHttpClient();
var request = HttpRequest.newBuilder()
    .uri(URI.create("${b2bUrlLiteral}"))
    .header("X-B2B-Client-Id", "{client_id}")
    .header("X-B2B-Client-Secret", "{client_secret}")
    .GET()
    .build();
var response = client.send(request, HttpResponse.BodyHandlers.ofString());
System.out.println(response.body());`}
                  {b2bApiHelpTab === "nodejs" &&
                    `const url = "${b2bUrlLiteral}";
const res = await fetch(url, {
  headers: {
    "X-B2B-Client-Id": "{client_id}",
    "X-B2B-Client-Secret": "{client_secret}",
  },
});
if (!res.ok) throw new Error(await res.text());
console.log(await res.text());`}
                  {b2bApiHelpTab === "curl" &&
                    `curl -sS "${b2bUrlLiteral}" \\
  -H "X-B2B-Client-Id: {client_id}" \\
  -H "X-B2B-Client-Secret: {client_secret}"`}
                </pre>
              </div>
            </div>
          </div>,
          document.body
        )}

    </div>
  );
}

function ResourceGroupsModal({
  isLight,
  onClose,
  credentials,
  onViewApplicationCredentials,
}: {
  isLight: boolean;
  onClose: () => void;
  credentials: ResourceAccessCredentialListItem[];
  onViewApplicationCredentials: (applicationId: string) => void;
}) {
  const [rows, setRows] = useState<ResourceAccessGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [isActiveNew, setIsActiveNew] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const list = await resourceAccessService.listGroups();
      setRows(list);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load groups");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const panelClass = isLight ? "bg-white border-neutral-200 text-neutral-900" : "bg-neutral-900 border-neutral-800 text-gray-200";
  const inputClass = isLight
    ? "bg-white border-neutral-300 text-neutral-900"
    : "bg-neutral-800 border-neutral-700 text-gray-200";

  const addGroup = async () => {
    const n = name.trim();
    if (!n || saving) return;
    setSaving(true);
    setError(null);
    try {
      await resourceAccessService.createGroup({ name: n, isActive: isActiveNew });
      setName("");
      setIsActiveNew(true);
      void load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Create failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: string) => {
    setSaving(true);
    setError(null);
    try {
      await resourceAccessService.deleteGroup(id);
      setDeleteId(null);
      void load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Delete failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/60 backdrop-blur-md">
      <div className={`border rounded-lg shadow-xl w-full max-w-lg max-h-[85vh] overflow-hidden flex flex-col ${panelClass}`}>
        <div
          className={`flex items-center justify-between px-4 py-3 border-b ${
            isLight ? "bg-neutral-50 border-neutral-200" : "bg-neutral-900 border-neutral-800"
          }`}
        >
          <h3 className={`font-semibold text-sm ${isLight ? "text-neutral-900" : "text-white"}`}>Applications</h3>
          <button type="button" onClick={onClose} className="p-1.5 rounded text-gray-400 hover:text-white hover:bg-neutral-800">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4 overflow-y-auto flex-1 space-y-4">
          {error && <div className="text-xs text-red-400 border border-red-800/50 rounded px-2 py-1">{error}</div>}

          <div className={`rounded border p-3 space-y-2 ${isLight ? "border-neutral-200" : "border-neutral-700"}`}>
            <p className="text-xs font-medium text-gray-400">Add Application</p>
            <input
              className={`w-full rounded px-3 py-2 text-xs outline-none border ${inputClass}`}
              placeholder="Application name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <label className="flex items-center gap-2 text-xs text-gray-400">
              <input type="checkbox" checked={isActiveNew} onChange={(e) => setIsActiveNew(e.target.checked)} />
              Active
            </label>
            <button
              type="button"
              disabled={saving || !name.trim()}
              onClick={() => void addGroup()}
              className="w-full py-2 rounded text-xs bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {saving ? "Saving…" : "Create application"}
            </button>
          </div>

          {loading ? (
            <div className="flex justify-center py-8 text-gray-500 text-xs">
              <Loader2 className="w-5 h-5 animate-spin" />
            </div>
          ) : (
            <ul className="space-y-3">
              {rows.map((g) => {
                const applicationRouteId = String(g.id || g.resourceGroupId || "").trim();
                const appCreds = findCredentialsForApplication(g, credentials);
                const multiCreds = appCreds.length > 1;
                return (
                  <li
                    key={applicationRouteId || g.resourceGroupName}
                    className={`border rounded px-3 py-2 text-xs space-y-2 ${
                      isLight ? "border-neutral-200" : "border-neutral-700"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-200 truncate">{g.resourceGroupName}</p>
                        <p className="text-[10px] text-gray-500 font-mono truncate">{g.resourceGroupId || g.id}</p>
                      </div>
                      <span className={g.isActive ? "text-green-400 text-[10px]" : "text-gray-500 text-[10px]"}>
                        {g.isActive ? "active" : "inactive"}
                      </span>
                      <button
                        type="button"
                        title="View credentials"
                        className={`p-1.5 rounded border transition-colors ${
                          isLight
                            ? "border-indigo-200 text-indigo-600 hover:bg-indigo-50"
                            : "border-indigo-700 text-indigo-300 hover:bg-indigo-950/50"
                        }`}
                        onClick={() => {
                          if (applicationRouteId) onViewApplicationCredentials(applicationRouteId);
                        }}
                        disabled={!applicationRouteId}
                      >
                        <Eye className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        title="Delete application"
                        className="text-red-400 p-1"
                        onClick={() => setDeleteId(applicationRouteId)}
                        disabled={saving || !applicationRouteId}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    {/* {multiCreds ? (
                      <div
                        className={`pt-2 border-t space-y-1.5 ${isLight ? "border-neutral-200" : "border-neutral-700/80"}`}
                      >
                        <p
                          className={`text-[10px] font-medium uppercase tracking-wide ${isLight ? "text-neutral-500" : "text-gray-500"}`}
                        >
                          Credentials
                        </p>
                        <ul className="space-y-1">
                          {appCreds.map((c) => (
                            <li key={c.id} className="flex items-center justify-end gap-2 min-w-0">
                              <span className="text-gray-300 truncate flex-1 text-left" title={c.label}>
                                {c.label}
                              </span>
                              <button
                                type="button"
                                title="View credentials"
                                onClick={() => {
                                  if (applicationRouteId) onViewApplicationCredentials(applicationRouteId);
                                }}
                                className={`flex-shrink-0 p-1.5 rounded border transition-colors ${
                                  isLight
                                    ? "border-indigo-200 text-indigo-600 hover:bg-indigo-50"
                                    : "border-indigo-700 text-indigo-300 hover:bg-indigo-950/50"
                                }`}
                              >
                                <Eye className="w-3.5 h-3.5" />
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : null} */}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        {deleteId &&
          createPortal(
            <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/50">
              <div className={`${panelClass} border rounded-lg p-4 max-w-xs w-full`}>
                <p className="text-xs text-gray-400 mb-3">Delete this resource group?</p>
                <div className="flex gap-2 justify-end">
                  <button type="button" className="text-xs px-2 py-1 border rounded border-neutral-600" onClick={() => setDeleteId(null)}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="text-xs px-2 py-1 rounded bg-red-600 text-white"
                    onClick={() => void remove(deleteId)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )}
      </div>
    </div>
  );
}
